import { type B2bAuditEntry, getB2bConfig, getB2bCustomer, getB2bCustomerCatalog, getB2bOrder, getB2bOrderFileUrl, isOrderDocument } from '@api/b2b';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';

import DispatchTab from './DispatchTab';
import DocumentsTab from './DocumentsTab';
import NextStepCard from './NextStepCard';
import PaymentsTab from './PaymentsTab';
import ReviewTab from './ReviewTab';
import { EDITABLE_STATUSES, money, units } from './utils';

export type B2bTabId = 'b2b-review' | 'b2b-payments' | 'b2b-dispatch' | 'b2b-documents';

/** One Activity timeline entry from the order's audit records (the Sales Order page draws it). */
export interface B2bActivityEvent {
    id: string;
    at: Date;
    title: string;
    meta: string;
}

/** What one audit record reads as on the timeline: who did what, and for a review, each change. */
function auditEvent(entry: B2bAuditEntry, index: number, name: (productId: string) => string): B2bActivityEvent {
    const who = entry.by ?? 'Someone';
    const at = new Date(entry.at);
    switch (entry.kind) {
        case 'PLACED':
            return {
                id: `audit-placed-${index}`,
                at,
                title: entry.onBehalf ? `Order placed by ${who} for the customer` : `Order placed by ${who} on the portal`,
                meta: [`${entry.lines ?? 0} lines`, `${units(entry.units ?? 0)} units`, `payable ${money(entry.payable ?? 0)}`].join(' · '),
            };
        case 'REVIEWED': {
            const changes = (entry.changes ?? []).map((c) => {
                if (c.change === 'ADDED') return `added ${name(c.productId)} × ${units(c.after?.quantity ?? 0)}`;
                if (c.change === 'REMOVED') return `removed ${name(c.productId)}`;
                const parts = [
                    c.before?.quantity !== c.after?.quantity ? `${units(c.before?.quantity ?? 0)} → ${units(c.after?.quantity ?? 0)} units` : null,
                    c.before?.unitPrice !== c.after?.unitPrice ? `price ${money(c.before?.unitPrice ?? 0)} → ${money(c.after?.unitPrice ?? 0)}` : null,
                ].filter(Boolean);
                return `${name(c.productId)}: ${parts.join(', ')}${c.reason ? ` (${c.reason})` : ''}`;
            });
            const total = entry.totalBefore !== entry.totalAfter ? `total ${money(entry.totalBefore ?? 0)} → ${money(entry.totalAfter ?? 0)}` : null;
            return {
                id: `audit-review-${index}`,
                at,
                title: `Reviewed by ${who}${changes.length ? ` · ${changes.length} line${changes.length === 1 ? '' : 's'} changed` : ''}`,
                meta: [...changes, total].filter(Boolean).join(' · ') || 'Free pieces adjusted',
            };
        }
        case 'PI_CONFIRMED':
            return {
                id: `audit-confirm-${index}`,
                at,
                title: entry.onBehalfOfCustomer ? `PI marked confirmed by ${who} for the customer` : `PI confirmed by ${who} on the portal`,
                meta: [entry.note, entry.fileKey ? 'screenshot on the Documents tab' : null].filter(Boolean).join(' · '),
            };
        case 'CANCELLED':
            return { id: `audit-cancel-${index}`, at, title: `Cancelled by ${who}`, meta: entry.reason ?? '' };
        default:
            return { id: `audit-short-${index}`, at, title: `Closed short by ${who}`, meta: entry.reason ?? '' };
    }
}

/**
 * The B2B customer steps on a Sales Order: present only when the order is on a B2B customer
 * channel (b2b_config.channelKeys). Marketplace orders get `isB2b: false` and render as before.
 */
export function useB2bOrderWorkspace(
    purchaseOrderId: string | undefined,
    channel: string | undefined,
    onOrderChanged?: () => void,
    openTab?: (tab: B2bTabId) => void,
) {
    const queryClient = useQueryClient();
    const { currentUser } = useCurrentUser();
    const isAdmin = currentUser?.role === 'ADMIN';

    const { data: config } = useQuery({ queryKey: ['b2b-config'], queryFn: getB2bConfig, staleTime: 5 * 60 * 1000, retry: false });
    const isB2b = !!channel && !!config?.channelKeys.includes(channel);
    const detailQuery = useQuery({
        queryKey: ['b2b-order', purchaseOrderId],
        queryFn: () => getB2bOrder(purchaseOrderId as string),
        enabled: isB2b && !!purchaseOrderId,
        // An order on the B2B channel that no customer placed (e.g. an older WMS-synced one) is
        // not a portal order: the B2B tabs simply do not appear.
        retry: false,
    });
    const detail = detailQuery.data;
    const customerId = detail?.order.b2bCustomerId;
    const { data: customer } = useQuery({ queryKey: ['b2b-customer', customerId], queryFn: () => getB2bCustomer(customerId as string), enabled: !!customerId });
    const { data: catalog } = useQuery({ queryKey: ['b2b-catalog', customerId], queryFn: () => getB2bCustomerCatalog(customerId as string), enabled: !!customerId });

    const refresh = () => {
        void queryClient.invalidateQueries({ queryKey: ['b2b-order', purchaseOrderId] });
        onOrderChanged?.();
    };

    const names = useMemo(() => {
        const map = new Map<string, string>();
        for (const p of catalog?.products ?? []) map.set(p.productId, `${p.name} (${p.sku})`);
        for (const i of detail?.order.items ?? []) if (!map.has(i.itemId)) map.set(i.itemId, i.description ?? i.itemId);
        return map;
    }, [catalog, detail]);

    const tabs = detail
        ? [
              { id: 'b2b-review', label: EDITABLE_STATUSES.has(detail.order.status) ? 'Review & PI' : 'Order lines' },
              { id: 'b2b-payments', label: `Payments (${detail.payments.payments.length})` },
              { id: 'b2b-dispatch', label: `Dispatch (${detail.dispatches.dispatches.filter((d) => d.status !== 'CANCELLED').length})` },
              { id: 'b2b-documents', label: `Documents (${detail.files.filter(isOrderDocument).length})` },
          ]
        : [];

    const content = (tab: string): React.ReactNode => {
        if (!detail) return null;
        switch (tab as B2bTabId) {
            case 'b2b-review':
                return (
                    <ReviewTab
                        detail={detail}
                        catalog={catalog ?? null}
                        isAdmin={isAdmin}
                        customerName={customer?.name ?? detail.order.vendorName ?? 'The customer'}
                        onSaved={refresh}
                    />
                );
            case 'b2b-payments':
                return <PaymentsTab detail={detail} onChanged={refresh} />;
            case 'b2b-dispatch':
                return <DispatchTab detail={detail} names={names} defaultShipFrom={customer?.shipFromWarehouseId ?? null} isAdmin={isAdmin} onChanged={refresh} />;
            case 'b2b-documents':
                return <DocumentsTab files={detail.files} open={(key) => getB2bOrderFileUrl(detail.order.purchaseOrderId, key)} />;
            default:
                return null;
        }
    };

    const rail = detail ? (
        <NextStepCard
            detail={detail}
            isAdmin={isAdmin}
            customerName={customer?.name ?? detail.order.vendorName}
            onChanged={refresh}
            onReview={() => openTab?.('b2b-review')}
        />
    ) : null;

    const activity = useMemo(() => (detail?.audit ?? []).map((entry, i) => auditEvent(entry, i, (id) => names.get(id) ?? id)), [detail, names]);

    // isPortalOrder: a customer placed it through the portal. isB2b alone only says the channel is
    // a B2B one, which a brand may share with orders its WMS synced (HealthFab: Offline).
    return { isB2b, isPortalOrder: !!detail, loading: isB2b && detailQuery.isLoading, tabs, content, rail, activity, isB2bTab: (tab: string) => tab.startsWith('b2b-') };
}

